import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NodePalette from '../components/journey/NodePalette';
import JourneyCanvas from '../components/journey/JourneyCanvas';
import ConfigDrawer from '../components/journey/ConfigDrawer';
import { defaultCfg } from '../data/nodeDefinitions';
import EventBus from '../utils/EventBus';
import { journeyService } from '../services/journeyService';
import { nodesToJourneyConfig, journeyConfigToNodes } from '../utils/journeyConfig';
import styles from './JourneyBuilder.module.css';

let _nodeId = 1;

class JourneyBuilder extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      journeyId: props.journeyId !== 'new' ? props.journeyId : null,
      name: 'New Journey',
      status: 'draft',
      nodes: [],
      edges: [],
      selectedId: null,
      drawerOpen: false,
      saving: false,
      loading: props.journeyId !== 'new',
    };
    this.addNode    = this.addNode.bind(this);
    this.moveNode   = this.moveNode.bind(this);
    this.deleteNode = this.deleteNode.bind(this);
    this.addEdge    = this.addEdge.bind(this);
    this.deleteEdge = this.deleteEdge.bind(this);
    this.selectNode = this.selectNode.bind(this);
    this.updateCfg  = this.updateCfg.bind(this);
    this.saveDraft  = this.saveDraft.bind(this);
    this.publish    = this.publish.bind(this);
  }

  componentDidMount() {
    if (!this.state.journeyId) return;
    journeyService.getById(this.state.journeyId)
      .then((journey) => {
        const { nodes, edges } = journeyConfigToNodes(journey.config);
        this.setState({
          loading: false,
          name: journey.name,
          status: journey.status,
          nodes,
          edges,
        });
      })
      .catch(() => {
        this.setState({ loading: false });
        EventBus.emit('toast', { type: 'error', message: 'Failed to load journey.' });
      });
  }

  addNode(type, x, y) {
    const id = 'n' + (_nodeId++);
    const px = x != null ? x : 60 + (this.state.nodes.length % 3) * 240;
    const py = y != null ? y : 60 + Math.floor(this.state.nodes.length / 3) * 160;
    this.setState(s => ({
      nodes: [...s.nodes, { id, type, x: px, y: py, cfg: defaultCfg(type) }],
    }));
  }

  moveNode(id, x, y) {
    this.setState(s => ({
      nodes: s.nodes.map(n => n.id === id ? { ...n, x, y } : n),
    }));
  }

  deleteNode(id) {
    this.setState(s => ({
      nodes: s.nodes.filter(n => n.id !== id),
      edges: s.edges.filter(e => e.from !== id && e.to !== id),
      selectedId: s.selectedId === id ? null : s.selectedId,
      drawerOpen: s.selectedId === id ? false : s.drawerOpen,
    }));
  }

  // Each output handle carries at most one edge — connecting a new one from
  // the same handle replaces whatever it used to point at, since the journey
  // engine only ever follows a single path out of any given step.
  addEdge(fromId, fromHandle, toId) {
    if (fromId === toId) return;
    const handle = fromHandle || 'out';
    const id = 'e' + Date.now() + '-' + Math.floor(Math.random() * 1e6);
    this.setState(s => ({
      edges: [
        ...s.edges.filter(e => !(e.from === fromId && (e.fromHandle || 'out') === handle)),
        { id, from: fromId, to: toId, fromHandle: handle },
      ],
    }));
  }

  deleteEdge(id) {
    this.setState(s => ({ edges: s.edges.filter(e => e.id !== id) }));
  }

  selectNode(id) {
    this.setState({ selectedId: id, drawerOpen: true });
  }

  updateCfg(newCfg) {
    const { selectedId } = this.state;
    this.setState(s => ({
      nodes: s.nodes.map(n => n.id === selectedId ? { ...n, cfg: newCfg } : n),
    }));
  }

  // Builds the backend config from the canvas, creating the journey on first
  // save and updating it thereafter. Returns the (possibly newly-created) id.
  _persist() {
    const { journeyId, name, nodes, edges } = this.state;
    const { config, skipped } = nodesToJourneyConfig(nodes, edges);
    if (skipped.length) {
      EventBus.emit('toast', { type: 'info', message: `Skipped unsupported step type(s): ${skipped.join(', ')}` });
    }

    if (journeyId) {
      return journeyService.update(journeyId, { name, config }).then(() => journeyId);
    }
    return journeyService.create({ name, config }).then((journey) => {
      this.setState({ journeyId: journey.id });
      this.props.onCreated?.(journey.id);
      return journey.id;
    });
  }

  saveDraft() {
    this.setState({ saving: true });
    try {
      this._persist()
        .then(() => {
          this.setState({ saving: false });
          EventBus.emit('toast', { type: 'success', message: 'Draft saved.' });
        })
        .catch((err) => {
          this.setState({ saving: false });
          EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to save draft.' });
        });
    } catch (err) {
      this.setState({ saving: false });
      EventBus.emit('toast', { type: 'error', message: err.message });
    }
  }

  publish() {
    if (!this.state.nodes.length) {
      EventBus.emit('toast', { type: 'error', message: 'Add at least one node before publishing.' });
      return;
    }
    this.setState({ saving: true });
    try {
      this._persist()
        .then((id) => journeyService.activate(id))
        .then((journey) => {
          this.setState({ saving: false, status: journey.status });
          EventBus.emit('toast', { type: 'success', message: '"' + this.state.name + '" published!' });
        })
        .catch((err) => {
          this.setState({ saving: false });
          EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to publish.' });
        });
    } catch (err) {
      this.setState({ saving: false });
      EventBus.emit('toast', { type: 'error', message: err.message });
    }
  }

  render() {
    const { name, status, nodes, edges, selectedId, drawerOpen, saving, loading } = this.state;
    const selNode = nodes.find(n => n.id === selectedId);

    if (loading) return <div className={styles.builder} />;

    return (
      <div className={styles.builder}>
        <div className={styles.topbar}>
          <div className={styles.titleWrap}>
            <input
              className={styles.titleInput}
              value={name}
              onChange={e => this.setState({ name: e.target.value })}
              title="Click to rename"
            />
            <span className={[styles.statusPill, styles['s-' + status]].join(' ')}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
          <div className={styles.tbRight}>
            <button className={styles.tbBtn} onClick={this.saveDraft} disabled={saving}>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button className={[styles.tbBtn, styles.tbPri].join(' ')} onClick={this.publish} disabled={saving}>
              ▶ Publish
            </button>
          </div>
        </div>

        <div className={styles.canvasWrap}>
          <NodePalette onAdd={this.addNode} />
          <JourneyCanvas
            nodes={nodes}
            edges={edges}
            selectedId={selectedId}
            onSelect={this.selectNode}
            onMove={this.moveNode}
            onDelete={this.deleteNode}
            onDeselect={() => this.setState({ selectedId: null, drawerOpen: false })}
            onConnect={this.addEdge}
            onEdgeDelete={this.deleteEdge}
            onAddAt={this.addNode}
          />
        </div>

        <ConfigDrawer
          open={drawerOpen}
          node={selNode}
          onClose={() => this.setState({ drawerOpen: false, selectedId: null })}
          onChange={this.updateCfg}
        />
      </div>
    );
  }
}

function JourneyBuilderWithRoute() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <JourneyBuilder
      journeyId={id}
      onCreated={(newId) => navigate('/journeys/' + newId + '/builder', { replace: true })}
    />
  );
}

export default JourneyBuilderWithRoute;
